import React from 'react';
import AccessoriesListControl from './AccessoriesListControl';
import { toast } from 'react-toastify';
import { calcRealHeight, SIZE_GAP } from './accessories.service';
import { defaultMoveMethod, rodsMoveMethod, ledsMoveMethod, polstMoveMethod } from './_utils/move';

function AccessoriesMethod(slug) {
  if (slug.includes('_dr_')) {
    return rodsMoveMethod;
  }

  if (slug.includes('_al_bi4k')) {
    return ledsMoveMethod;
  }

  if (slug.includes('_polst_')) {
    return polstMoveMethod;
  }

  return defaultMoveMethod;
}

function NotifyContent({ name }) {
  return (
    <div>
      <h5>Akcja niedozwolona</h5>
      <p>
        Ten ruch wpływa na położenie akcesorium: <strong>{name}</strong>. Usuń akcesoria lub ułóż je
        w inny sposób.
      </p>
    </div>
  );
}

function Notify({ name }) {
  toast.info(<NotifyContent name={name} />, {
    closeOnClick: true,
    position: 'top-right',
    autoClose: 10000,
    toastId: 1, // show only one
  });
}

function findLast(array, predicate) {
  for (let i = array.length - 1; i >= 0; --i) {
    const x = array[i];
    if (predicate(x)) {
      return x;
    }
  }
}

function SelectedAccessories({ accessories, wardrobe }) {
  const accessoriesLength = accessories.length;
  const updateList = wardrobe.update;

  const deleteItem = el => {
    if (el.slug.includes('_polst_')) {
      const lastItem = findLast(accessories, item => item.slug === el.slug);
      const updatedAccessories = accessories.filter(item => item !== lastItem);

      updateList(updatedAccessories);
    } else {
      const updatedAccessories = accessories.filter(item => item !== el);

      updateList(updatedAccessories);
    }
  };

  const duplicateItem = item => {
    wardrobe.accessories.add(item);
  };

  const checkNextItem = (list, index) => {
    if (index < list.length - 1) {
      const item = list[index];
      const nextItem = list[index + 1];

      if (item.actualHeight > nextItem.altitude) {
        const { position } = nextItem;
        const [, max] = position;
        const altitude = item.actualHeight;
        if (altitude <= max) {
          const itemWithNewPosition = {
            ...nextItem,
            altitude: altitude,
            actualHeight: calcRealHeight(
              altitude + nextItem.protective_field + nextItem.dimensions.y,
            ),
          };
          list[index + 1] = itemWithNewPosition;
          return checkNextItem(list, index + 1);
        }
        Notify(nextItem);
        return null;
      }
      return list;
    }
    return list;
  };

  const returnItemWhenSwitchedPosition = (prevAltitude, item, index) => {
    const method = AccessoriesMethod(item.slug);

    const altitude = method.switchAltitude(prevAltitude, item, index);

    return {
      ...item,
      altitude: altitude,
      actualHeight: calcRealHeight(altitude + item.protective_field + item.dimensions.y),
    };
  };

  const switchItems = index => {
    const copyAccessories = [...accessories];
    const actualItem = accessories[index];
    const nextItem = accessories[index + 1];
    const prevItem = index >= 1 ? accessories[index - 1] : null;
    const { position } = actualItem;
    const [, max] = position;
    const isLast = index + 1 === accessories.length - 1;

    let item = returnItemWhenSwitchedPosition(actualItem.altitude, nextItem, index);
    if (isLast && prevItem) {
      item = returnItemWhenSwitchedPosition(prevItem.actualHeight, nextItem, index);
    }

    if (max < item.actualHeight) {
      return;
    }

    copyAccessories[index] = item;
    copyAccessories[index + 1] = returnItemWhenSwitchedPosition(
      item.actualHeight,
      actualItem,
      index + 1,
    );

    const checkResult = checkNextItem(copyAccessories, index + 1);

    if (checkResult) {
      updateList(copyAccessories);
    }
  };

  const moveUp = (item, index) => {
    const methods = AccessoriesMethod(item.slug);
    methods.moveUp(item, accessories, index);
    updateList(accessories);
  };

  const moveDown = (item, index) => {
    const methods = AccessoriesMethod(item.slug);
    methods.moveDown(item, accessories, index);
    updateList(accessories);
  };

  const isNextElem = index => index + 1 < accessoriesLength;
  const isPrevElem = index => index > 0;

  const checkAltitudeWithNextElement = (item, index) => {
    const nextItem = accessories[index + 1];
    const newMaxRange = item.altitude + item.protective_field + item.dimensions.y + SIZE_GAP;
    if (item.slug.includes('_dr_') && nextItem.slug.includes('_dr_')) {
      return false;
    }

    if (newMaxRange >= nextItem.altitude) {
      switchItems(index);
    } else {
      moveUp(item, index);
    }
  };

  const checkAltitudeWithPrevElement = (item, index) => {
    const prevItem = accessories[index - 1];

    if (item.slug.includes('_dr_') && prevItem.slug.includes('_dr_')) {
      return;
    }

    if (item.altitude - SIZE_GAP < prevItem.actualHeight || item.slug.includes('_dr_')) {
      switchItems(index - 1);
    } else {
      moveDown(item, index);
    }
  };

  const moveUpItem = (elem, index) => {
    const { position, altitude } = elem;
    const [, max] = position;
    if (altitude >= max) {
      return;
    }
    if (isNextElem(index)) {
      checkAltitudeWithNextElement(elem, index);
    } else {
      moveUp(elem, index);
    }
  };

  const moveDownItem = (elem, index) => {
    if (isPrevElem(index)) {
      checkAltitudeWithPrevElement(elem, index);
    } else {
      moveDown(elem, index);
    }
  };

  const changePosition = (elem, index, direct) => {
    if (direct === 'up') {
      moveUpItem(elem, index);
    } else {
      moveDownItem(elem, index);
    }
  };

  const checkMoveUpItem = (elem, index) => {
    const method = AccessoriesMethod(elem.slug);
    if (method.moveUpCondition(elem)) {
      return true;
    }
    return false;
  };

  const checkMoveDownItem = (elem, index) => {
    const method = AccessoriesMethod(elem.slug);

    if (method.moveDownCondition(elem)) {
      return true;
    }

    return false;
  };

  const checkPossibility = (elem, index, direct) => {
    if (direct === 'up') {
      return checkMoveUpItem(elem, index);
    } else {
      return checkMoveDownItem(elem, index);
    }
  };

  return (
    <>
      {accessories.map((item, index) => (
        <AccessoriesListControl
          key={item.uId}
          item={item}
          index={index}
          deleteItem={() => deleteItem(item)}
          wardrobe={wardrobe}
          duplicateItem={duplicateItem}
          changePosition={changePosition}
          checkPossibility={checkPossibility}
        />
      ))}
    </>
  );
}

export default SelectedAccessories;
